import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import PaymentModal from './PaymentModal';
import { parseUTCDate } from '../lib/dateUtils';
import { useAuth } from '../App'; // Add useAuth import
import { Search } from 'lucide-react';

const BookingsPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, refreshUser } = useAuth(); // Add user context and refreshUser
  
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancellingBooking, setCancellingBooking] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentModal, setPaymentModal] = useState({
    isOpen: false,
    booking: null
  });
  const [dinnerModal, setDinnerModal] = useState({
    isOpen: false,
    booking: null,
    attending: false,
    partner: false,
    saving: false
  });

  const canChangeDinner = (booking) => {
    if (!booking.event_dinner_enabled || booking.status !== 'confirmed') return false;
    if (booking.event_dinner_cutoff) {
      const cutoff = parseUTCDate(booking.event_dinner_cutoff);
      if (cutoff && new Date() > cutoff) return false;
    }
    return true;
  };

  const handleSaveDinner = async () => {
    const { booking, partner } = dinnerModal;
    setDinnerModal(prev => ({ ...prev, saving: true }));
    try {
      const response = await axios.put(`/bookings/${booking.id}/dinner`, {
        attending: true,
        partner
      });
      if (response.data.payment_form_data) {
        toast({ title: "Redirecting to PayFast", description: `R${response.data.amount.toFixed(2)} card payment required for the dinner addition` });
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = response.data.payment_form_action;
        Object.entries(response.data.payment_form_data).forEach(([key, value]) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = value;
          form.appendChild(input);
        });
        document.body.appendChild(form);
        form.submit();
        return;
      }
      toast({
        title: "Dinner Updated",
        description: response.data.refunded > 0
          ? `R${response.data.refunded.toFixed(2)} refunded to your credits`
          : (response.data.credits_used > 0 ? `R${response.data.credits_used.toFixed(2)} paid with credits` : 'Your dinner choice has been saved')
      });
      setDinnerModal({ isOpen: false, booking: null, attending: false, partner: false, saving: false });
      await fetchBookings();
    } catch (error) {
      toast({ variant: "destructive", title: "Error", description: error.response?.data?.detail || "Failed to update dinner" });
      setDinnerModal(prev => ({ ...prev, saving: false }));
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await axios.get('/bookings');
      setBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load bookings"
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePayNow = (booking) => {
    setPaymentModal({
      isOpen: true,
      booking: booking
    });
  };

  const handlePaymentSuccess = () => {
    setPaymentModal({
      isOpen: false,
      booking: null
    });
    // Refresh bookings to update status
    fetchBookings();
    // Refresh user data to update credits balance
    refreshUser();
    toast({
      title: "Payment Initiated",
      description: "You've been redirected to complete payment. Your booking will be confirmed once payment is complete."
    });
  };

  const handleCancelBooking = async (bookingId) => {
    try {
      // Log current user state before cancellation
      console.log('🔍 handleCancelBooking: Starting cancellation');
      console.log('🔍 handleCancelBooking: Current user before cancel:', user?.id, user?.first_name, user?.last_name);
      console.log('🔍 handleCancelBooking: Cancelling booking:', bookingId);
      
      const cancelResponse = await axios.delete(`/bookings/${bookingId}`);
      console.log('🔍 handleCancelBooking: Cancel response:', cancelResponse.data);
      
      toast({
        title: "Booking Cancelled",
        description: "Your booking has been cancelled successfully"
      });
      
      // Refresh bookings list
      console.log('🔍 handleCancelBooking: Refreshing bookings list...');
      await fetchBookings();
      console.log('🔍 handleCancelBooking: Bookings refreshed');
      
      // Refresh user data to update credits balance after refund
      console.log('🔍 handleCancelBooking: Refreshing user data...');
      console.log('🔍 handleCancelBooking: User before refreshUser:', user?.id, user?.first_name);
      await refreshUser();
      console.log('🔍 handleCancelBooking: User after refreshUser:', user?.id, user?.first_name);
      
      setCancellingBooking(null);
      console.log('🔍 handleCancelBooking: Cancellation complete');
    } catch (error) {
      console.error('❌ handleCancelBooking: Error:', error);
      toast({
        variant: "destructive",
        title: "Cancellation Failed",
        description: error.response?.data?.detail || "Failed to cancel booking"
      });
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      confirmed: { className: 'bg-green-100 text-green-800', label: 'Confirmed' },
      pending: { className: 'bg-yellow-100 text-yellow-800', label: 'Pending Payment' },
      cancelled: { className: 'bg-red-100 text-red-800', label: 'Cancelled' },
      completed: { className: 'bg-gray-100 text-gray-800', label: 'Completed' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <Badge className={config.className}>
        {config.label}
      </Badge>
    );
  };

  const canCancelBooking = (booking) => {
    // Use backend-provided cancellation permission
    return booking.can_cancel || false;
  };

  const getConfiscationWarning = (booking) => {
    const teeTime = new Date(booking.tee_time);
    const now = new Date();
    const hoursUntilTeeTime = (teeTime - now) / (1000 * 60 * 60);
    
    return hoursUntilTeeTime < 48;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading {user?.role === 'admin' ? 'all' : 'your'} bookings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900" data-testid="bookings-title">
              My Bookings
            </h1>
            <p className="text-gray-600 mt-2">
              Manage your tee time reservations
            </p>
          </div>
          <Button 
            className="bg-emerald-600 hover:bg-emerald-700"
            onClick={() => navigate('/dashboard')}
            data-testid="back-to-dashboard-btn"
          >
            Back to Dashboard
          </Button>
        </div>

        {/* Search and Filter Bar */}
        <div className="mb-6 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search by event name or booking reference..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="booking-search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-40" data-testid="booking-status-filter">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Bookings</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {bookings.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl text-emerald-600">📅</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Bookings Yet</h3>
              <p className="text-gray-600 mb-6">
                You haven't made any tee time reservations. Book your first round today!
              </p>
              <Button 
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={() => navigate('/dashboard')}
                data-testid="book-first-round-btn"
              >
                Book Your First Round
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {bookings
              .filter((booking) => {
                // Search filter
                const searchLower = searchTerm.toLowerCase();
                const matchesSearch = !searchTerm || 
                  (booking.event_name && booking.event_name.toLowerCase().includes(searchLower)) ||
                  (booking.booking_reference && booking.booking_reference.toLowerCase().includes(searchLower));
                
                // Status filter
                const matchesStatus = statusFilter === 'all' || booking.status === statusFilter;
                
                return matchesSearch && matchesStatus;
              })
              .map((booking) => (
              <Card key={booking.id} className="hover:shadow-lg transition-shadow duration-200">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        Booking #{booking.booking_reference}
                      </h3>
                      {/* Enhanced booking type indicator */}
                      {booking.booking_type === 'booked_for_other' && (
                        <div className="text-sm text-blue-600 font-medium mt-1">
                          📋 You booked this for {booking.booked_for_name}
                        </div>
                      )}
                      {booking.booking_type === 'booked_for_you' && (
                        <div className="text-sm text-green-600 font-medium mt-1">
                          🎁 {booking.booked_by_name || 'Someone'} booked this for you
                        </div>
                      )}
                      <div className="flex items-center gap-4 mt-2">
                        {getStatusBadge(booking.status)}
                        <span className="text-sm text-gray-500">
                          Created {parseUTCDate(booking.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-semibold text-gray-900">
                        R{booking.total_amount.toFixed(2)}
                      </div>
                      {booking.credits_used > 0 && (
                        <div className="text-sm text-green-600">
                          Credits used: R{booking.credits_used.toFixed(2)}
                        </div>
                      )}
                      {booking.free_round_used && (
                        <div className="text-sm text-purple-600">
                          Free round applied
                        </div>
                      )}
                    </div>
                  </div>

                  <div className={`grid grid-cols-1 ${user?.role === 'admin' ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-4 mb-4`}>
                    {user?.role === 'admin' && (
                      <div>
                        <div className="text-sm text-gray-500">User</div>
                        <div className="font-medium">
                          {booking.user_name || 'Unknown User'}
                        </div>
                        <div className="text-xs text-gray-400">
                          {booking.user_email}
                        </div>
                        <div className="text-xs text-blue-600 capitalize">
                          {booking.user_membership}
                        </div>
                      </div>
                    )}
                    <div>
                      <div className="text-sm text-gray-500">Event</div>
                      <div className="font-medium">
                        {booking.event_name || 'Unknown Event'}
                      </div>
                      {user?.role === 'admin' && booking.event_date && (
                        <div className="text-xs text-gray-400">
                          {new Date(booking.event_date).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Tee Time</div>
                      <div className="font-medium">
                        {new Date(booking.tee_time).toLocaleString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true
                        })}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Players</div>
                      <div className="font-medium">
                        {booking.players_count} player{booking.players_count > 1 ? 's' : ''}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Cart</div>
                      <div className="font-medium">
                        {booking.cart_required ? 'Included' : 'Not required'}
                      </div>
                    </div>
                    {booking.event_dinner_enabled && (
                      <div data-testid={`dinner-status-${booking.id}`}>
                        <div className="text-sm text-gray-500">🍽️ Prize Giving Dinner</div>
                        <div className="font-medium">
                          {booking.dinner_attending
                            ? `Attending${booking.dinner_partner ? ' + partner' : ''} (R${(booking.dinner_fee || 0).toFixed(2)})`
                            : 'Included'}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end gap-3">
                    {canChangeDinner(booking) && (
                      <Button
                        variant="outline"
                        className="border-purple-600 text-purple-600 hover:bg-purple-50"
                        data-testid={`dinner-change-${booking.id}`}
                        onClick={() => setDinnerModal({
                          isOpen: true,
                          booking,
                          attending: true,
                          partner: !!booking.dinner_partner,
                          saving: false
                        })}
                      >
                        🍽️ Partner
                      </Button>
                    )}
                    {booking.status === 'pending' && booking.total_amount > 0 && (
                      <Button 
                        variant="outline"
                        className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                        data-testid={`pay-booking-${booking.id}`}
                        onClick={() => handlePayNow(booking)}
                      >
                        Pay Now
                      </Button>
                    )}
                    
                    {canCancelBooking(booking) && (
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button 
                            variant="outline" 
                            className="border-red-600 text-red-600 hover:bg-red-50"
                            data-testid={`cancel-booking-${booking.id}`}
                          >
                            Cancel Booking
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Cancel Booking</DialogTitle>
                          </DialogHeader>
                          <div className="py-4">
                            {/* Enhanced cancellation message based on booking type */}
                            {booking.booking_type === 'booked_for_other' ? (
                              <p className="text-gray-600 mb-4">
                                Are you sure you want to cancel the booking you made for <strong>{booking.booked_for_name}</strong>?
                                <br />
                                <span className="text-sm text-gray-500">Booking #{booking.booking_reference}</span>
                              </p>
                            ) : booking.booking_type === 'booked_for_you' ? (
                              <p className="text-gray-600 mb-4">
                                Are you sure you want to cancel this booking that <strong>{booking.booked_by_name || 'someone'}</strong> made for you?
                                <br />
                                <span className="text-sm text-gray-500">Booking #{booking.booking_reference}</span>
                              </p>
                            ) : (
                              <p className="text-gray-600 mb-4">
                                Are you sure you want to cancel booking #{booking.booking_reference}?
                              </p>
                            )}
                            
                            {/* 48-Hour Forfeit Warning */}
                            {getConfiscationWarning(booking) && (
                              <Alert className="mb-4 border-orange-200 bg-orange-50">
                                <AlertDescription className="text-orange-800">
                                  <strong>⚠️ Within 48 Hours Notice:</strong> Payment will be forfeited and no refunds processed.
                                  {booking.booking_type === 'booked_for_other' && (
                                    <> {booking.booked_for_name} will be notified of the cancellation.</>
                                  )}
                                </AlertDescription>
                              </Alert>
                            )}
                            
                            {user?.role === 'admin' && booking.user_name && (
                              <Alert className="mb-4">
                                <AlertDescription>
                                  <strong>User:</strong> {booking.user_name} ({booking.user_email})
                                </AlertDescription>
                              </Alert>
                            )}
                            
                            {/* Show refund info only if MORE than 48 hours */}
                            {!getConfiscationWarning(booking) && booking.credits_used > 0 && (
                              <Alert className="mb-4">
                                <AlertDescription>
                                  R{booking.credits_used.toFixed(2)} in credits will be refunded to your account.
                                </AlertDescription>
                              </Alert>
                            )}
                            {!getConfiscationWarning(booking) && booking.free_round_used && (
                              <Alert className="mb-4">
                                <AlertDescription>
                                  Your free round will be credited back to your account.
                                </AlertDescription>
                              </Alert>
                            )}
                            <div className="flex gap-3">
                              <Button
                                variant="outline"
                                onClick={() => setCancellingBooking(null)}
                                className="flex-1"
                              >
                                Keep Booking
                              </Button>
                              <Button
                                variant="destructive"
                                onClick={() => handleCancelBooking(booking.id)}
                                className="flex-1"
                                data-testid={`confirm-cancel-${booking.id}`}
                              >
                                Cancel Booking
                              </Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    )}
                    
                    {!canCancelBooking(booking) && booking.status !== 'cancelled' && (
                      <div className="text-sm text-gray-500 flex items-center">
                        Booking completed or cancelled
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Prize Giving Dinner Change Modal */}
      <Dialog open={dinnerModal.isOpen} onOpenChange={(open) => !open && setDinnerModal({ isOpen: false, booking: null, attending: false, partner: false, saving: false })}>
        <DialogContent className="max-w-md" data-testid="dinner-change-modal">
          <DialogHeader>
            <DialogTitle>🍽️ Prize Giving Dinner</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              🍽️ Dinner attendance is <span className="font-medium">included for all players</span> at this event.
            </p>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={dinnerModal.partner}
                onChange={(e) => setDinnerModal(prev => ({ ...prev, partner: e.target.checked }))}
                className="rounded"
                data-testid="dinner-modal-partner"
              />
              <span className="text-sm">I am bringing a partner</span>
            </label>
            <p className="text-xs text-gray-500">
              Adding a partner is paid with your credits (or card if credits don't cover it). Removing a partner refunds your credits (no refund within 48 hours of your tee time).
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDinnerModal({ isOpen: false, booking: null, attending: false, partner: false, saving: false })}>Cancel</Button>
              <Button onClick={handleSaveDinner} disabled={dinnerModal.saving} data-testid="dinner-modal-save">
                {dinnerModal.saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Modal */}
      <PaymentModal
        isOpen={paymentModal.isOpen}
        onClose={() => setPaymentModal({isOpen: false, booking: null})}
        amount={paymentModal.booking?.total_amount}
        description={paymentModal.booking ? `Booking #${paymentModal.booking.booking_reference}` : ''}
        onPaymentSuccess={handlePaymentSuccess}
        paymentType={{
          type: "booking-payment",
          bookingId: paymentModal.booking?.id,
          eventId: paymentModal.booking?.event_id
        }}
        user={user}
        allowCredits={true}
        allowFreeRounds={true}
        eventData={{
          free_rounds_allowed: paymentModal.booking?.event_free_rounds_allowed ?? true,
          name: paymentModal.booking?.event_name
        }}
      />
    </div>
  );
};

export default BookingsPage;